import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";

export function useDocumentGeneration() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();

  const generateDocument = async (title: string, content: string, caseId?: string, documentType: string = 'generated') => {
    if (!user?.id) {
      throw new Error("Du må være logget inn for å generere dokumenter.");
    }

    setIsGenerating(true);
    setProgress(10);

    try {
      console.log("Generating document with RAG...");
      const { data: generatedData, error: generationError } = await supabase.functions
        .invoke('generate-document-rag', {
          body: {
            title,
            content,
            documentType,
            caseId
          }
        });

      if (generationError) throw generationError;
      setProgress(50);

      if (!generatedData?.content) {
        throw new Error("Ingen innhold generert fra RAG-tjenesten");
      }

      console.log("Saving generated document...");
      const { data: document, error: saveError } = await supabase
        .from('documents')
        .insert({
          title,
          content: generatedData.content,
          document_type: documentType,
          case_id: caseId || null,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (saveError) throw saveError;
      setProgress(100);

      return document;
    } catch (error: any) {
      console.error('Error in document generation:', error);
      toast({
        variant: "destructive",
        title: "Feil ved dokumentgenerering",
        description: error.message || "Kunne ikke generere dokumentet. Vennligst prøv igjen."
      });
      throw error;
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  return {
    generateDocument,
    isGenerating,
    progress
  };
}